/**
 * The agent's highlight, made of what the agent is made of: grains of the orb's sand gather onto an element's outline,
 * drift along it under a travelling brightness wave, then scatter. It follows the element as the page scrolls.
 */
import { sandSprites } from './grain'

const TAU = Math.PI * 2
const LIFE_MS = 4000
const GATHER_MS = 650
const SCATTER_MS = 750
/** Grains sit just outside the outline, so the sand frames what it points at and never covers it. */
const GAP = 7
const BAND = 5
const GRAINS_PER_PX = 2.6
const MAX_GRAINS = 2600
const DUST = 0.14
const DPR_MAX = 2

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))
const easeOut = (value: number) => 1 - (1 - value) ** 3

type OutlinePoint = { x: number; y: number; nx: number; ny: number }

function onEdge(out: OutlinePoint, x: number, y: number, nx: number, ny: number) {
  out.x = x
  out.y = y
  out.nx = nx
  out.ny = ny
}

function onCorner(out: OutlinePoint, cx: number, cy: number, r: number, angle: number) {
  const nx = Math.cos(angle)
  const ny = Math.sin(angle)
  onEdge(out, cx + nx * r, cy + ny * r, nx, ny)
}

/** The point `along` (in pixels, clockwise from the top left) a rounded rectangle's edge, and its outward normal. */
function onOutline(along: number, w: number, h: number, r: number, out: OutlinePoint) {
  const sideW = w - 2 * r
  const sideH = h - 2 * r
  const arc = (TAU / 4) * r
  let left = along

  if (left < sideW) return onEdge(out, r + left, 0, 0, -1)

  left -= sideW

  if (left < arc) return onCorner(out, w - r, r, r, -TAU / 4 + left / r)

  left -= arc

  if (left < sideH) return onEdge(out, w, r + left, 1, 0)

  left -= sideH

  if (left < arc) return onCorner(out, w - r, h - r, r, left / r)

  left -= arc

  if (left < sideW) return onEdge(out, w - r - left, h, 0, 1)

  left -= sideW

  if (left < arc) return onCorner(out, r, h - r, r, TAU / 4 + left / r)

  left -= arc

  if (left < sideH) return onEdge(out, 0, h - r - left, -1, 0)

  onCorner(out, r, r, r, TAU / 2 + (left - sideH) / r)
}

export function sandRing(el: HTMLElement) {
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches
  const canvas = document.createElement('canvas')
  canvas.setAttribute('aria-hidden', 'true')
  canvas.setAttribute('data-cogent-point', '')
  canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;z-index:2147483100;pointer-events:none'
  document.body.append(canvas)
  const context = canvas.getContext('2d')!
  const sprites = sandSprites()
  const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0
  const first = el.getBoundingClientRect()
  const count = Math.min(MAX_GRAINS, Math.round(2 * (first.width + first.height) * GRAINS_PER_PX) + 240)
  const grains = Array.from({ length: count }, () => {
    const dust = Math.random() < DUST
    const angle = Math.random() * TAU
    const reach = 18 + Math.random() * 60
    return {
      at: Math.random(),
      off: dust ? 4 + Math.random() * 18 : (Math.random() + Math.random() - 1) * BAND,
      flow: (still ? 0 : 1) * (0.008 + Math.random() * 0.018),
      alpha: dust ? 0.32 + Math.random() * 0.2 : 0.62 + Math.random() * 0.38,
      size: (dust ? 0.75 : 1) * (1.5 + Math.random() * 1.1),
      sprite: sprites[Math.floor(Math.random() * sprites.length)]!,
      delay: Math.random() * 220,
      fromX: still ? 0 : Math.cos(angle) * reach,
      fromY: still ? 0 : Math.sin(angle) * reach,
      away: still ? 0 : 10 + Math.random() * 34,
    }
  })
  const point: OutlinePoint = { x: 0, y: 0, nx: 0, ny: 0 }
  const start = performance.now()
  const draw = (now: number) => {
    const age = now - start

    if (age > LIFE_MS || !el.isConnected) return canvas.remove()

    const dpr = Math.min(DPR_MAX, devicePixelRatio || 1)
    const width = Math.round(innerWidth * dpr)
    const height = Math.round(innerHeight * dpr)

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    } else context.clearRect(0, 0, width, height)

    const box = el.getBoundingClientRect()
    const w = box.width + 2 * GAP
    const h = box.height + 2 * GAP
    const r = Math.min(radius + GAP, w / 2, h / 2)
    const perimeter = 2 * (w + h - 4 * r) + TAU * r
    const leaving = clamp01((age - (LIFE_MS - SCATTER_MS)) / SCATTER_MS)

    for (const grain of grains) {
      const gathered = still ? clamp01(age / GATHER_MS) : easeOut(clamp01((age - grain.delay) / GATHER_MS))
      const at = (grain.at + grain.flow * (age / 1000)) % 1
      onOutline(at * perimeter, w, h, r, point)
      const push = grain.off + leaving * leaving * grain.away
      const x = box.left - GAP + point.x + point.nx * push + grain.fromX * (1 - gathered)
      const y = box.top - GAP + point.y + point.ny * push + grain.fromY * (1 - gathered)

      if (x < -4 || y < -4 || x > innerWidth + 4 || y > innerHeight + 4) continue

      // The orb's wave: crests catch the light, a trough thins the sand without opening a hole.
      const wave = still ? 1 : Math.min(1.5, Math.max(0.65, 1 + 0.55 * Math.sin(TAU * (at * 3 - age / 1500))))
      context.globalAlpha = Math.min(1, grain.alpha * wave * gathered * (1 - leaving))
      const size = grain.size * dpr
      context.drawImage(grain.sprite, x * dpr - size / 2, y * dpr - size / 2, size, size)
    }

    requestAnimationFrame(draw)
  }
  requestAnimationFrame(draw)
}
